package com.yendo.mx.ui.home

import androidx.compose.runtime.*
import com.yendo.mx.models.*
import com.yendo.mx.repository.AuthRepository
import com.yendo.mx.repository.MercadoPagoRepository
import com.yendo.mx.utils.*
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import dev.gitlive.firebase.firestore.firestore
import dev.gitlive.firebase.firestore.where
import kotlinx.coroutines.flow.collectLatest
import com.yendo.mx.utils.getAhora
import kotlinx.coroutines.launch
import kotlinx.datetime.*
import kotlinx.datetime.Clock as KClock
import kotlin.math.roundToInt

import kotlinx.serialization.encodeToString
import androidx.lifecycle.SavedStateHandle
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope

class EnviosViewModel(private val savedStateHandle: SavedStateHandle) : ViewModel() {
    private val db = Firebase.firestore
    private val auth = Firebase.auth

    var tiposPaquete by mutableStateOf<List<String>>(emptyList())
    var vehiculos by mutableStateOf<List<String>>(emptyList())

    // 🚀 BLINDAJE ATÓMICO: Getter y Setter respaldados automáticamente por SavedStateHandle
    private val json = kotlinx.serialization.json.Json { ignoreUnknownKeys = true }

    var origen: UbicacionPedido?
        get() = savedStateHandle.get<String>("origen_json")?.let { try { json.decodeFromString<UbicacionPedido>(it) } catch(e: Exception) { null } }
        set(value) {
            savedStateHandle["origen_json"] = value?.let { json.encodeToString(it) }
            calcularCotizacion()
        }

    fun updateOrigen(value: UbicacionPedido?) {
        origen = value
    }

    var destino: UbicacionPedido?
        get() = savedStateHandle.get<String>("destino_json")?.let { try { json.decodeFromString<UbicacionPedido>(it) } catch(e: Exception) { null } }
        set(value) {
            savedStateHandle["destino_json"] = value?.let { json.encodeToString(it) }
            calcularCotizacion()
        }

    fun updateDestino(value: UbicacionPedido?) {
        destino = value
    }

    // 🚀 REFACTOR: Usamos mutableStateOf respaldado por SavedStateHandle para reactividad real en Compose.
    
    var indicacionesOrigen by mutableStateOf(savedStateHandle.get<String>("indicacionesOrigen") ?: "")
        private set
    fun updateIndicacionesOrigen(value: String) { indicacionesOrigen = value; savedStateHandle["indicacionesOrigen"] = value }

    var indicacionesDestino by mutableStateOf(savedStateHandle.get<String>("indicacionesDestino") ?: "")
        private set
    fun updateIndicacionesDestino(value: String) { indicacionesDestino = value; savedStateHandle["indicacionesDestino"] = value }

    var tipoPaqueteSelected by mutableStateOf(savedStateHandle.get<String>("tipoPaqueteSelected") ?: "")
        private set
    fun updateTipoPaqueteSelected(value: String) { tipoPaqueteSelected = value; savedStateHandle["tipoPaqueteSelected"] = value }

    var vehiculoSelected by mutableStateOf(savedStateHandle.get<String>("vehiculoSelected") ?: "")
        private set
    fun updateVehiculoSelected(value: String) { vehiculoSelected = value; savedStateHandle["vehiculoSelected"] = value }

    var rolEnvio by mutableStateOf(savedStateHandle.get<String>("rolEnvio") ?: "ENVIANDO")
        private set
    fun updateRolEnvio(value: String) { rolEnvio = value; savedStateHandle["rolEnvio"] = value }

    var nombreContactoContacto by mutableStateOf(savedStateHandle.get<String>("nombreContactoContacto") ?: "")
        private set
    fun updateNombreContactoContacto(value: String) { nombreContactoContacto = value; savedStateHandle["nombreContactoContacto"] = value }

    var telefonoContacto by mutableStateOf(savedStateHandle.get<String>("telefonoContacto") ?: "")
        private set
    fun updateTelefonoContacto(value: String) { telefonoContacto = value; savedStateHandle["telefonoContacto"] = value }

    var requierePin by mutableStateOf(savedStateHandle.get<Boolean>("requierePin") ?: false)
        private set
    fun updateRequierePin(value: Boolean) { requierePin = value; savedStateHandle["requierePin"] = value }

    var tipoTiempo by mutableStateOf(savedStateHandle.get<String>("tipoTiempo") ?: "ASAP")
        private set
    fun updateTipoTiempo(value: String) { tipoTiempo = value; savedStateHandle["tipoTiempo"] = value }

    var diaProgramado by mutableStateOf(savedStateHandle.get<String>("diaProgramado") ?: "Hoy")
        private set
    fun updateDiaProgramado(value: String) { diaProgramado = value; savedStateHandle["diaProgramado"] = value }

    var horaProgramada by mutableStateOf(savedStateHandle.get<String>("horaProgramada") ?: "")
        private set
    fun updateHoraProgramada(value: String) { horaProgramada = value; savedStateHandle["horaProgramada"] = value }

    var metodoPago by mutableStateOf(savedStateHandle.get<String>("metodoPago") ?: "Efectivo")
        private set
    fun updateMetodoPago(value: String) { metodoPago = value; savedStateHandle["metodoPago"] = value }

    var quienPagaEfectivo by mutableStateOf(savedStateHandle.get<String>("quienPagaEfectivo") ?: "REMITENTE")
        private set
    fun updateQuienPagaEfectivo(value: String) { quienPagaEfectivo = value; savedStateHandle["quienPagaEfectivo"] = value }

    var cambioEfectivoSeleccionado by mutableStateOf<Double?>(savedStateHandle.get<Double>("cambioEfectivoSeleccionado"))
        private set
    fun updateCambioEfectivoSeleccionado(value: Double?) { cambioEfectivoSeleccionado = value; savedStateHandle["cambioEfectivoSeleccionado"] = value }

    var aceptaTerminos by mutableStateOf(false) 

    // 🚀 NUEVOS ESTADOS PARA DECLARACIÓN DE PAQUETE
    var categoriaPaquete by mutableStateOf("Documentos")
    var descripcionPaquete by mutableStateOf("")
    var fotoPaqueteBytes by mutableStateOf<ByteArray?>(null)

    var cuponInput by mutableStateOf(savedStateHandle.get<String>("cuponInput") ?: "")
        private set
    fun updateCuponInput(value: String) { cuponInput = value; savedStateHandle["cuponInput"] = value }

    fun limpiarFormulario() {
        updateOrigen(null)
        updateDestino(null)
        updateIndicacionesOrigen("")
        updateIndicacionesDestino("")
        updateTipoPaqueteSelected(tiposPaquete.firstOrNull() ?: "")
        updateVehiculoSelected(vehiculos.firstOrNull() ?: "")
        updateRolEnvio("ENVIANDO")
        updateNombreContactoContacto("")
        updateTelefonoContacto("")
        updateRequierePin(false)
        updateTipoTiempo("ASAP")
        updateDiaProgramado("Hoy")
        updateHoraProgramada("")
        updateMetodoPago("Efectivo")
        updateQuienPagaEfectivo("REMITENTE")
        updateCambioEfectivoSeleccionado(null)
        updateCuponInput("")
        
        // 🚀 LIMPIEZA DE NUEVOS CAMPOS
        categoriaPaquete = "Documentos"
        descripcionPaquete = ""
        fotoPaqueteBytes = null
        aceptaTerminos = false
        
        // Limpieza profunda del SavedStateHandle
        savedStateHandle.remove<String>("origen_json")
        savedStateHandle.remove<String>("destino_json")
        savedStateHandle.remove<String>("indicacionesOrigen")
        savedStateHandle.remove<String>("indicacionesDestino")
        savedStateHandle.remove<String>("tipoPaqueteSelected")
        savedStateHandle.remove<String>("vehiculoSelected")
        savedStateHandle.remove<String>("rolEnvio")
        savedStateHandle.remove<String>("nombreContactoContacto")
        savedStateHandle.remove<String>("telefonoContacto")
        savedStateHandle.remove<Boolean>("requierePin")
        savedStateHandle.remove<String>("tipoTiempo")
        savedStateHandle.remove<String>("diaProgramado")
        savedStateHandle.remove<String>("horaProgramada")
        savedStateHandle.remove<String>("metodoPago")
        savedStateHandle.remove<String>("quienPagaEfectivo")
        savedStateHandle.remove<Double>("cambioEfectivoSeleccionado")
        savedStateHandle.remove<String>("cuponInput")
    }

    var descuentoAplicado by mutableStateOf(0.0)
    var patrocinadorCupon by mutableStateOf("")
    var mensajeCupon by mutableStateOf("")

    var distanciaSimulada by mutableStateOf(0.0)
    var costoEnvioPuro by mutableStateOf(0.0)
    var tarifaServicioApp by mutableStateOf(0.0)
    var precioCotizado by mutableStateOf(0.0)

    val esPlataformaCerrada: Boolean
        get() = !LogisticaUtils.estaPlataformaAbierta(ConfigManager.configActiva)

    val isEmergencyBlocked: Boolean = false 
    val mensajeEmergencia: String = "" 

    val totalPagar: Double
        get() = maxOf(0.0, redondear(precioCotizado - descuentoAplicado))

    var cargando by mutableStateOf(false)
    var error by mutableStateOf<String?>(null)
    var pedidoExitosoId by mutableStateOf<String?>(null)

    // 🚀 LÓGICA DE CVV PARA TARJETAS GUARDADAS
    var mostrarDialogoCVV by mutableStateOf(false)
    var cvvInput by mutableStateOf("")

    init {
        viewModelScope.launch(kotlinx.coroutines.Dispatchers.Default) {
            ConfigManager.estadoConfig.collectLatest { estado ->
                if (estado is com.yendo.mx.utils.EstadoCarga.Exito) {
                    val config = estado.config.finanzas_cliente
                    
                    // 🚀 CATEGORÍAS DE PAQUETE DINÁMICAS (CHIPS ENVÍOS) DESDE YENDO OS ADMIN
                    val tiposConfig = config.mandaditos_tipos_paquete.filter { it.isNotBlank() }
                    tiposPaquete = if (tiposConfig.isNotEmpty()) tiposConfig else listOf("Documentos", "Paquete Chico", "Paquete Mediano", "Otros")
                    
                    // 🚀 FILTRO DINÁMICO DE VEHÍCULOS: Excluir Bici y vehículos desactivados en Yendo OS Admin
                    val desactivados = config.mandaditos_vehiculos_desactivados
                    vehiculos = config.multiplicadores_vehiculo.keys.toList()
                        .filter { !it.contains("Bici", ignoreCase = true) && !desactivados.contains(it) }
                        .sorted()

                    if (tipoPaqueteSelected.isEmpty() || !tiposPaquete.contains(tipoPaqueteSelected)) {
                        updateTipoPaqueteSelected(tiposPaquete.firstOrNull() ?: "Documentos")
                    }
                    if (vehiculoSelected.isEmpty() || !vehiculos.contains(vehiculoSelected)) {
                        updateVehiculoSelected(vehiculos.firstOrNull() ?: "Moto")
                    }

                    calcularCotizacion()
                }
            }
        }
    }


    fun calcularCotizacion() {
        val o = origen
        val d = destino
        if (o == null || d == null) return

        // 🚀 1. CÁLCULO INMEDIATO HAVERSINE COMO FALLBACK UI
        val distHaversine = GeoUtils.calcularDistanciaKm(o.latitude, o.longitude, d.latitude, d.longitude)
        distanciaSimulada = redondear(distHaversine)
        recalcularPrecioCotizado()

        // 🚀 2. CONSULTA ASÍNCRONA DE DISTANCIA REAL POR CALLES (OSRM)
        viewModelScope.launch {
            val distCalle = com.yendo.mx.repository.RutaRepository.obtenerDistanciaKm(o.latitude, o.longitude, d.latitude, d.longitude)
            if (distCalle > 0) {
                distanciaSimulada = redondear(distCalle)
                recalcularPrecioCotizado()
            }
        }
    }

    private fun recalcularPrecioCotizado() {
        val config = ConfigManager.configActiva?.finanzas_cliente

        // 🚀 MULTIPLICADOR DE VEHÍCULO DEFENSIVO (Sin alterar ConfiguracionZona)
        val multiplicador = when {
            vehiculoSelected.contains("Auto", ignoreCase = true) -> 1.5
            vehiculoSelected.contains("Camioneta", ignoreCase = true) -> 2.0
            vehiculoSelected.contains("Bici", ignoreCase = true) -> 0.8
            else -> config?.multiplicadores_vehiculo?.get(vehiculoSelected) ?: 1.0
        }

        // 🚀 LECTURA DEFENSIVA CON FALLBACKS NATIVOS DE LA ZONA
        val tarifaInicial = config?.mandaditos_tarifa_inicial?.toDouble() 
            ?: config?.tarifa_inicial?.toDouble() 
            ?: 15.0

        val costoKm = config?.mandaditos_costo_km?.toDouble() 
            ?: config?.costo_km?.toDouble() 
            ?: 10.0

        val envioMinimo = config?.mandaditos_envio_minimo?.toDouble() 
            ?: config?.envio_minimo?.toDouble() 
            ?: 25.0

        val tarifaServicio = config?.mandaditos_tarifa_servicio_fija?.toDouble() 
            ?: config?.tarifa_servicio_fija?.toDouble() 
            ?: 5.0

        val costoBase = tarifaInicial + (distanciaSimulada * costoKm)
        val subtotalVehiculo = costoBase * multiplicador

        // Lo que gana el repartidor (Garantizado el mínimo)
        costoEnvioPuro = redondear(maxOf(envioMinimo, subtotalVehiculo))

        // Lógica excluyente de servicio de la app
        tarifaServicioApp = redondear(tarifaServicio)

        precioCotizado = redondear(costoEnvioPuro + tarifaServicioApp)

        if (descuentoAplicado > 0.0 && cuponInput.isNotBlank()) aplicarCupon()
        error = null
    }

    fun aplicarCupon() {
        val uid = auth.currentUser?.uid ?: return
        val zonaActual = ConfigManager.configActiva?.id_zona

        viewModelScope.launch {
            if (cuponInput.isBlank()) {
                mensajeCupon = "Ingresa un código"
                descuentoAplicado = 0.0
                patrocinadorCupon = ""
                return@launch
            }
            mensajeCupon = "Validando..."

            try {
                val snapshot = db.collection("Cupones").document(cuponInput).get()
                if (!snapshot.exists) {
                    descuentoAplicado = 0.0; patrocinadorCupon = ""; mensajeCupon = "Cupón no existe o está mal escrito."
                    return@launch
                }

                val cuponInfo = snapshot.data<CuponFirebase>()

                if (!cuponInfo.activo) { descuentoAplicado = 0.0; mensajeCupon = "Este cupón está desactivado o ya expiró."; return@launch }
                
                // 🚀 VALIDACIÓN DE MÓDULO (SEGMENTACIÓN DE CUPONES)
                // Si la lista no está vacía, DEBE contener "ENVIOS" para funcionar aquí.
                if (cuponInfo.modulosValidos.isNotEmpty() && !cuponInfo.modulosValidos.contains("ENVIOS")) {
                    descuentoAplicado = 0.0
                    mensajeCupon = "Este cupón no aplica para Mandaditos/Envíos."
                    return@launch
                }

                if (precioCotizado < cuponInfo.consumoMinimo) { descuentoAplicado = 0.0; mensajeCupon = "Requiere consumo mínimo de $${cuponInfo.consumoMinimo}"; return@launch }
                if (cuponInfo.metodosValidos.isNotEmpty() && !cuponInfo.metodosValidos.contains(metodoPago)) { descuentoAplicado = 0.0; mensajeCupon = "Aplica pagando con: ${cuponInfo.metodosValidos.joinToString(", ")}"; return@launch }
                if (cuponInfo.idZonaExclusiva.isNotBlank() && zonaActual != cuponInfo.idZonaExclusiva) { descuentoAplicado = 0.0; mensajeCupon = "Este cupón no es válido para tu ciudad."; return@launch }
                if (cuponInfo.usosActuales >= cuponInfo.limiteUsosGlobal) { descuentoAplicado = 0.0; mensajeCupon = "¡Ups! Este cupón se ha agotado."; return@launch }

                val usosPrevios = db.collection("Pedidos").where { "idUsuario" equalTo uid }.where { "idCuponUsado" equalTo cuponInput }.get()
                if (usosPrevios.documents.size >= cuponInfo.limitePorUsuario) { descuentoAplicado = 0.0; mensajeCupon = "Ya has usado este cupón el máximo de veces permitido."; return@launch }

                if (cuponInfo.soloPrimerPedido) {
                    val pedidosAnteriores = db.collection("Pedidos").where { "idCliente" equalTo uid }.get()
                    if (pedidosAnteriores.documents.isNotEmpty()) { descuentoAplicado = 0.0; mensajeCupon = "Este cupón es exclusivo para tu primera compra."; return@launch }
                }

                descuentoAplicado = when (cuponInfo.tipo.uppercase()) {
                    "CANTIDAD" -> cuponInfo.valor
                    "PORCENTAJE" -> precioCotizado * (cuponInfo.valor / 100.0)
                    "ENVIO" -> costoEnvioPuro
                    else -> 0.0
                }

                if (descuentoAplicado > precioCotizado) descuentoAplicado = precioCotizado
                patrocinadorCupon = cuponInfo.quienPaga
                mensajeCupon = "¡Cupón aplicado! -$${redondear(descuentoAplicado)}"
            } catch (e: Exception) {
                descuentoAplicado = 0.0; patrocinadorCupon = ""; mensajeCupon = "Error de red al validar."
            }
        }
    }

    fun confirmarEnvio(cardIdPersistente: String?, tokenTemporal: String?, issuerId: String? = null, comprobanteBytes: ByteArray? = null) {
        // 🚀 VALIDACIÓN DE DISPONIBILIDAD (Horario Global)
        if (!LogisticaUtils.estaPlataformaAbierta(ConfigManager.configActiva)) {
            error = "Yendo está cerrado actualmente. Consulta el horario de hoy."
            return
        }

        // En lugar de checkbox, lo hacemos automático al dar clic
        aceptaTerminos = true
        if (metodoPago == "Efectivo" && cambioEfectivoSeleccionado == null) { error = "Selecciona con cuánto vas a pagar"; return }
        
        // 🚀 NUEVA LÓGICA: Si es tarjeta guardada, pedir CVV antes de seguir
        if (metodoPago == "Tarjeta") {
            if (cardIdPersistente == null && tokenTemporal == null) { error = "Selecciona una tarjeta válida"; return }
            
            // 🚀 EL CANDADO DEFINITIVO: Si no tenemos token temporal aún, exigimos el CVV.
            if (tokenTemporal == null && cardIdPersistente != null) {
                mostrarDialogoCVV = true
                return
            }
        }

        if (metodoPago == "Transferencia" && comprobanteBytes == null) {
            error = "Adjunta tu comprobante de pago para continuar"
            return
        }

        val telDigits = telefonoContacto.filter { it.isDigit() }
        if (nombreContactoContacto.isBlank()) { error = "Ingresa el nombre del contacto"; return }
        if (telDigits.length != 10) { error = "El teléfono del contacto debe ser de 10 dígitos (${telDigits.length}/10)"; return }
        if (tipoTiempo == "Programado" && horaProgramada.isBlank()) { error = "Selecciona una hora para tu envío"; return }

        val user = auth.currentUser ?: run { error = "Usuario no autenticado"; return }
        // 🚀 RECUPERAMOS DATOS REALES DEL USUARIO
        val perfil = AuthRepository.usuarioActual.value

        cargando = true
        viewModelScope.launch {
            try {
                val pedidoRef = db.collection("Pedidos").document
                val idPedido = pedidoRef.id
                val pin = if (requierePin) (1000..9999).random().toString() else null

                // 🚀 CARGA DE COMPROBANTE SI APLICA
                var urlComprobante: String? = null
                if (comprobanteBytes != null) {
                    urlComprobante = PedidosManager.subirComprobantePago(idPedido, comprobanteBytes)
                }

                // 🚀 CARGA DE FOTO DE PAQUETE SI APLICA
                var urlFotoPaquete: String? = null
                if (fotoPaqueteBytes != null) {
                    urlFotoPaquete = PedidosManager.subirFotoPaquete(idPedido, fotoPaqueteBytes!!)
                }

                // Lógica de hora programada
                val zonaHoraria = TimeZone.currentSystemDefault()
                val now = Instant.fromEpochMilliseconds(getAhora())
                val currentMs = now.toEpochMilliseconds()
                val fechaFinalProgramada = if (tipoTiempo == "Programado") {
                    val fechaBase = if (diaProgramado == "Mañana") now.plus(24, DateTimeUnit.HOUR) else now
                    val horaParts = horaProgramada.split(":")
                    if (horaParts.size == 2) {
                        val localDt = fechaBase.toLocalDateTime(zonaHoraria)
                        LocalDateTime(
                            localDt.year,
                            localDt.month,
                            localDt.dayOfMonth,
                            horaParts[0].toInt(),
                            horaParts[1].toInt()
                        ).toInstant(zonaHoraria).toEpochMilliseconds()
                    } else currentMs
                } else null

                val idGenerado = PedidosManager.enviarPedido(
                    tipoPedido = "MANDADITO",
                    idZona = ConfigManager.configActiva?.id_zona?.takeIf { it.isNotBlank() } ?: "arriaga", // 🚀 INYECTADO PARA EL BACKEND
                    esProgramado = tipoTiempo == "Programado",
                    fechaProgramada = fechaFinalProgramada,
                    subtotal = 0.0, // 🚀 REGLA CONTABLE: El subtotal de comida es cero en Mandaditos
                    costoEnvio = costoEnvioPuro,
                    tarifaServicio = tarifaServicioApp,
                    propina = 0.0,
                    descuento = descuentoAplicado,
                    totalPagado = totalPagar,
                    metodoPago = metodoPago,
                    conCuantoPaga = if (metodoPago == "Efectivo") cambioEfectivoSeleccionado else null,
                    cambioAEntregar = if (metodoPago == "Efectivo" && cambioEfectivoSeleccionado != null) {
                        val dif = cambioEfectivoSeleccionado!! - totalPagar
                        if (dif > 0) kotlin.math.round(dif * 100.0) / 100.0 else 0.0
                    } else null,
                    nombreRestaurante = "Envío Express ($categoriaPaquete)",
                    logoRestaurante = "", // 🚀 MANDADITOS NO LLEVAN LOGO DE RESTAURANTE
                    direccionNegocio = origen?.direccion ?: "",
                    latNegocio = origen?.latitude ?: 0.0,
                    lngNegocio = origen?.longitude ?: 0.0,
                    indicacionesNegocio = indicacionesOrigen, // 🚀 FIX LOGÍSTICO: Pasamos las indicaciones del Punto A
                    direccionEntrega = destino?.direccion ?: "",
                    latitud = destino?.latitude ?: 0.0,
                    longitud = destino?.longitude ?: 0.0,
                    tipoUbicacion = "Mandadito",
                    opcionEntrega = "Mandadito",
                    indicaciones = indicacionesDestino,
                    
                    // 🚀 DATOS PARA MP SCORE
                    nombreCliente = perfil?.nombre ?: "",
                    apellidoCliente = perfil?.apellido ?: "",
                    emailCliente = perfil?.email ?: user.email ?: "",
                    telefonoCliente = perfil?.telefono?.ifBlank { user.phoneNumber ?: "" } ?: (user.phoneNumber ?: ""),
                    issuerId = issuerId,

                    idTarjetaSeleccionada = cardIdPersistente,
                    tokenPagar = tokenTemporal, // 🚀 Inyectamos el token fresco para el cobro
                    idCuponUsado = if (descuentoAplicado > 0.0) cuponInput else null
                )

                if (idGenerado != null) {
                    // 🚀 ACTUALIZAMOS DATOS ESPECÍFICOS DE MANDADITO
                    val updates = mutableMapOf<String, Any?>(
                        "telefonoCliente" to (perfil?.telefono?.ifBlank { user.phoneNumber ?: "" } ?: (user.phoneNumber ?: "")),
                        "requiresPin" to requierePin, // 🔥 FIX PIN KEY
                        "requierePreAutorizacion" to true,
                        "datosMandadito.categoria" to categoriaPaquete,
                        "datosMandadito.descripcion" to descripcionPaquete,
                        "datosMandadito.pinSeguridad" to pin,
                        "datosMandadito.rolEnvio" to rolEnvio,
                        "datosMandadito.nombreContacto" to nombreContactoContacto,
                        "datosMandadito.telefonoContacto" to telefonoContacto,
                        "datosMandadito.nombreReceptor" to (if(rolEnvio == "ENVIANDO") nombreContactoContacto else perfil?.nombre),
                        "datosMandadito.telefonoReceptor" to (if(rolEnvio == "ENVIANDO") telefonoContacto else (perfil?.telefono?.ifBlank { user.phoneNumber ?: "" } ?: "")),
                        "datosMandadito.quienPaga" to quienPagaEfectivo, // 🚀 RESPONSABILIDAD DE PAGO
                        "contratoFinanciero.montoCobroACliente" to totalPagar,
                        "contratoFinanciero.recaudadorComision" to quienPagaEfectivo,
                        "contratoFinanciero.netoRepartidor" to costoEnvioPuro,
                        "contratoFinanciero.netoYendoMx" to tarifaServicioApp
                    )
                    
                    if (urlComprobante != null) {
                        updates["estadoActual"] = "REVISANDO_COMPROBANTE"
                        updates["urlComprobante"] = urlComprobante
                    }
                    if (urlFotoPaquete != null) {
                        updates["datosMandadito.url_foto"] = urlFotoPaquete // 🔥 FIX PHOTO KEY
                    }
                    
                    db.collection("Pedidos").document(idGenerado).update(*(updates.toList().toTypedArray()))

                    pedidoExitosoId = idGenerado
                } else {
                    error = "Error al crear el pedido"
                }
                cargando = false
            } catch (e: Exception) {
                error = "Error al enviar: ${e.message}"
                cargando = false
            }
        }
    }

    fun actualizarUbicacion(isOrigen: Boolean, lat: Double, lng: Double, direccion: String) {
        val ubi = UbicacionPedido(direccion = direccion, latitude = lat, longitude = lng)
        if (isOrigen) updateOrigen(ubi) else updateDestino(ubi)
        if (origen != null && destino != null) calcularCotizacion()
    }

    fun procesarPagoConCVV(cardId: String, issuerId: String? = null) {
        if (cvvInput.length < 3) {
            error = "Ingresa un CVV válido"
            return
        }
        
        cargando = true
        mostrarDialogoCVV = false
        
        viewModelScope.launch {
            try {
                val nuevoToken = MercadoPagoRepository.generarTokenConTarjetaGuardada(cardId, cvvInput)
                cvvInput = "" // 🚀 LIMPIAMOS LA MEMORIA
                
                if (nuevoToken != null) {
                    confirmarEnvio(cardId, nuevoToken, issuerId)
                } else {
                    error = "No se pudo validar la tarjeta. Revisa tu CVV."
                    cargando = false
                }
            } catch (e: Exception) {
                error = "Error al procesar pago: ${e.message}"
                cvvInput = "" // 🚀 LIMPIAMOS LA MEMORIA
                cargando = false
            }
        }
    }

    // 🚀 LÓGICA DE INTERSECCIÓN DE HORARIOS
    fun obtenerHorariosDisponibles(esMañana: Boolean): List<String> {
        val config = ConfigManager.configActiva ?: return emptyList()
        val zonaHoraria = TimeZone.currentSystemDefault()
        val ahoraInstant = Instant.fromEpochMilliseconds(getAhora())
        val objetivoInstant = if (esMañana) ahoraInstant.plus(24, DateTimeUnit.HOUR) else ahoraInstant
        val fechaObjetivo = objetivoInstant.toLocalDateTime(zonaHoraria)

        // 1. Buscamos el horario del día objetivo (Hoy o Mañana)
        val nombreDia = when(fechaObjetivo.dayOfWeek) {
            DayOfWeek.MONDAY -> "LUNES"
            DayOfWeek.TUESDAY -> "MARTES"
            DayOfWeek.WEDNESDAY -> "MIERCOLES"
            DayOfWeek.THURSDAY -> "JUEVES"
            DayOfWeek.FRIDAY -> "VIERNES"
            DayOfWeek.SATURDAY -> "SABADO"
            DayOfWeek.SUNDAY -> "DOMINGO"
        }

        val horarioDia = config.operacion.horario_semanal[nombreDia] ?: return emptyList()
        if (!horarioDia.activo) return emptyList()

        val horaFinPlataforma = horarioDia.cierre
        val horaInicioPlataforma = horarioDia.apertura

        val listaHoras = mutableListOf<String>()
        
        // Si es hoy, empezamos 60 min después de "ahora" (1 hora de anticipación). 
        // Si es mañana, empezamos desde la apertura.
        val minutosInicio = if (esMañana) {
            LogisticaUtils.aMinutos(horaInicioPlataforma)
        } else {
            val minActualesMasBuffer = fechaObjetivo.hour * 60 + fechaObjetivo.minute + 60
            val minApertura = LogisticaUtils.aMinutos(horaInicioPlataforma)
            maxOf(minActualesMasBuffer, minApertura)
        }

        val minutosFin = LogisticaUtils.aMinutos(horaFinPlataforma)

        var actual = if (minutosInicio % 30 == 0) minutosInicio else ((minutosInicio / 30) + 1) * 30
        while (actual <= minutosFin) {
            val h = actual / 60
            val m = actual % 60
            listaHoras.add("${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}")
            actual += 30
        }

        return listaHoras
    }
}
